import { CoreGraphNode } from "../../../../core/graph/CoreGraphNode";
import { ParamType } from "../../../poly/ParamType";
import { ParamConstructorByType } from "../../../params/types/ParamConstructorByType";
import { NodeEvent } from "../../../poly/NodeEvent";
import { Poly } from "../../../Poly";
const NODE_SIMPLE_NAME = "params";
export class ParamsController {
  constructor(node) {
    this.node = node;
    this._param_create_mode = false;
    this._params_created = false;
    this._params_by_name = {};
    this._params_list = [];
    this._param_names = [];
    this._non_spare_params = [];
    this._spare_params = [];
    this._non_spare_param_names = [];
    this._spare_param_names = [];
    this._params_added_since_last_params_eval = false;
    this._promises = [];
  }
  dispose() {
    if (this._params_node) {
      this._params_node.dispose();
    }
    for (let param of this.all) {
      param.dispose();
    }
    this._post_create_params_hook_names = void 0;
    this._post_create_params_hooks = void 0;
    this._on_scene_load_hooks = void 0;
    this._on_scene_load_hook_names = void 0;
  }
  initDependencyNode() {
    if (!this._params_node) {
      this._params_node = new CoreGraphNode(this.node.scene(), NODE_SIMPLE_NAME);
      this.node.addGraphInput(this._params_node, false);
    }
  }
  init() {
    this.initDependencyNode();
    this._param_create_mode = true;
    this._initFromParamsConfig();
    this.node.createParams();
    this._postCreateParams();
  }
  _postCreateParams() {
    this._updateCaches();
    this._initParamAccessors();
    this._param_create_mode = false;
    this._params_created = true;
    this._runPostCreateParamsHooks();
  }
  postCreateSpareParams() {
    this._updateCaches();
    this._initParamAccessors();
    this.node.scene().referencesController.notifyParamsUpdated(this.node);
    this.node.emit(NodeEvent.PARAMS_UPDATED);
  }
  updateParams(options) {
    let has_created_a_param = false;
    let has_deleted_a_param = false;
    const addedParams = [];
    if (options.namesToDelete) {
      for (let param_name of options.namesToDelete) {
        if (this.has(param_name)) {
          this._deleteParam(param_name);
          has_deleted_a_param = true;
        }
      }
    }
    if (options.toAdd) {
      for (let param_data of options.toAdd) {
        const param = this.addParam(param_data.type, param_data.name, param_data.initValue, param_data.options);
        if (param) {
          if (param_data.rawInput != null) {
            param.set(param_data.rawInput);
          }
          has_created_a_param = true;
          addedParams.push(param);
        }
      }
    }
    if (has_deleted_a_param || has_created_a_param) {
      this.postCreateSpareParams();
    }
    for (let param of addedParams) {
      this.node.scene().missingExpressionReferencesController.checkForMissingParamReferences(param);
    }
  }
  _initFromParamsConfig() {
    var _a;
    const paramsConfig = this.node.paramsConfig;
    let init_values_used = false;
    if (paramsConfig) {
      for (let name of Object.keys(paramsConfig)) {
        const config = paramsConfig[name];
        let init_value;
        const paramsInitValueOverrides = (_a = this.node.createOptions) == null ? void 0 : _a.paramsInitValueOverrides;
        if (paramsInitValueOverrides) {
          init_value = paramsInitValueOverrides[name];
          init_values_used = true;
        }
        this.addParam(config.type, name, config.init_value, config.options, init_value);
      }
    }
    if (init_values_used) {
      this.node.setDirty();
    }
    if (this.node.createOptions) {
      this.node.createOptions.paramsInitValueOverrides = void 0;
    }
  }
  _initParamAccessors() {
    let current_names_in_accessor = Object.getOwnPropertyNames(this.node.pv);
    this._removeUnneededAccessors(current_names_in_accessor);
    current_names_in_accessor = Object.getOwnPropertyNames(this.node.pv);
    for (let param of this.all) {
      const is_spare = param.options.isSpare();
      const param_not_yet_in_accessors = !current_names_in_accessor.includes(param.name());
      if (param_not_yet_in_accessors || is_spare) {
        Object.defineProperty(this.node.pv, param.name(), {
          get: () => {
            return param.value;
          },
          configurable: is_spare
        });
        Object.defineProperty(this.node.p, param.name(), {
          get: () => {
            return param;
          },
          configurable: is_spare
        });
      }
    }
  }
  _removeUnneededAccessors(current_names_in_accessor) {
    const current_param_names = this._param_names;
    const names_to_remove = [];
    for (let current_name_in_accessor of current_names_in_accessor) {
      if (!current_param_names.includes(current_name_in_accessor)) {
        names_to_remove.push(current_name_in_accessor);
      }
    }
    for (let name_to_remove of names_to_remove) {
      Object.defineProperty(this.node.pv, name_to_remove, {
        get: () => {
          return void 0;
        },
        configurable: true
      });
      Object.defineProperty(this.node.p, name_to_remove, {
        get: () => {
          return void 0;
        },
        configurable: true
      });
    }
  }
  get params_node() {
    return this._params_node;
  }
  get all() {
    return this._params_list;
  }
  get non_spare() {
    return this._non_spare_params;
  }
  get spare() {
    return this._spare_params;
  }
  get names() {
    return this._param_names;
  }
  get non_spare_names() {
    return this._non_spare_param_names;
  }
  get spare_names() {
    return this._spare_param_names;
  }
  set_with_type(param_name, value, type) {
    const param = this.paramWithType(param_name, type);
    if (param) {
      param.set(value);
    } else {
      Poly.warn(`param ${param_name} not found with type ${type}`);
    }
  }
  set_float(param_name, value) {
    this.set_with_type(param_name, value, ParamType.FLOAT);
  }
  set_vector3(param_name, value) {
    this.set_with_type(param_name, value, ParamType.VECTOR3);
  }
  has_param(param_name) {
    return this._params_by_name[param_name] != null;
  }
  has(param_name) {
    return this.has_param(param_name);
  }
  get(param_name) {
    return this.param(param_name);
  }
  paramWithType(param_name, type) {
    const param = this.param(param_name);
    if (param && param.type() == type) {
      return param;
    }
  }
  getFloat(param_name) {
    return this.paramWithType(param_name, ParamType.FLOAT);
  }
  value(param_name) {
    var _a;
    return (_a = this.param(param_name)) == null ? void 0 : _a.value;
  }
  valueWithType(param_name, type) {
    var _a;
    return (_a = this.paramWithType(param_name, type)) == null ? void 0 : _a.value;
  }
  boolean(paramName) {
    return this.valueWithType(paramName, ParamType.BOOLEAN);
  }
  float(paramName) {
    return this.valueWithType(paramName, ParamType.FLOAT);
  }
  integer(paramName) {
    return this.valueWithType(paramName, ParamType.INTEGER);
  }
  string(paramName) {
    return this.valueWithType(paramName, ParamType.STRING);
  }
  vector2(paramName) {
    return this.valueWithType(paramName, ParamType.VECTOR2);
  }
  vector3(paramName) {
    return this.valueWithType(paramName, ParamType.VECTOR3);
  }
  color(paramName) {
    return this.valueWithType(paramName, ParamType.COLOR);
  }
  param(paramName) {
    const p = this._params_by_name[paramName];
    if (p != null) {
      return p;
    } else {
      Poly.warn(`tried to access param '${paramName}' in node ${this.node.path()}, but existing params are: ${this.names} on node ${this.node.path()}`);
      return null;
    }
  }
  _deleteParam(param_name) {
    const param = this._params_by_name[param_name];
    if (param) {
      if (this._params_node) {
        this._params_node.removeGraphInput(this._params_by_name[param_name]);
      }
      param._setupNodeDependencies(null);
      delete this._params_by_name[param_name];
      if (param.isMultiple() && param.components) {
        for (let component of param.components) {
          const child_name = component.name();
          delete this._params_by_name[child_name];
        }
      }
      param.dispose();
    } else {
      throw new Error(`param '${param_name}' does not exist on node ${this.node.path()}`);
    }
  }
  addParam(type, paramName, defaultValue, options = {}, initData) {
    const isSpare = options["spare"] || false;
    if (this._param_create_mode === false && !isSpare) {
      Poly.warn(`node ${this.node.path()} (${this.node.type()}) param '${paramName}' cannot be created outside of createParams`);
    }
    if (this.node.scene() == null) {
      Poly.warn(`node ${this.node.path()} (${this.node.type()}) has no scene assigned`);
    }
    const constructor = ParamConstructorByType[type];
    if (constructor != null) {
      const existing_param = this._params_by_name[paramName];
      if (existing_param) {
        if (isSpare) {
          if (existing_param.type() != type) {
            this._deleteParam(existing_param.name());
          }
        } else {
          Poly.warn(`a param named ${paramName} already exists`, this.node);
        }
      }
      const param = new constructor(this.node.scene(), this.node);
      param.options.set(options);
      param.setName(paramName);
      param.setInitValue(defaultValue);
      param.initComponents();
      if (initData == null) {
        param.set(defaultValue);
      } else {
        if (param.options.isExpressionForEntities()) {
          param.set(defaultValue);
        }
        if (initData.raw_input != null) {
          param.set(initData.raw_input);
        } else {
          if (initData.simple_data != null) {
            param.set(initData.simple_data);
          } else {
            if (initData.complex_data != null) {
              const raw_input = initData.complex_data.raw_input;
              if (raw_input) {
                param.set(raw_input);
              } else {
                param.set(defaultValue);
              }
              const overriden_options = initData.complex_data.overriden_options;
              if (overriden_options != null) {
                const keys = Object.keys(overriden_options);
                for (let key of keys) {
                  param.options.setOption(key, overriden_options[key]);
                }
              }
            }
          }
        }
      }
      param._setupNodeDependencies(this.node);
      this._params_by_name[param.name()] = param;
      if (param.isMultiple() && param.components) {
        for (let component of param.components) {
          this._params_by_name[component.name()] = component;
        }
      }
      this._params_added_since_last_params_eval = true;
      return param;
    }
  }
  _updateCaches() {
    this._params_list = Object.values(this._params_by_name);
    this._param_names = Object.keys(this._params_by_name);
    this._non_spare_params = Object.values(this._params_by_name).filter((p) => !p.options.isSpare());
    this._spare_params = Object.values(this._params_by_name).filter((p) => p.options.isSpare());
    this._non_spare_param_names = Object.values(this._params_by_name).filter((p) => !p.options.isSpare()).map((p) => p.name());
    this._spare_param_names = Object.values(this._params_by_name).filter((p) => p.options.isSpare()).map((p) => p.name());
  }
  async _evalParam(param) {
    if (param.isDirty()) {
      await param.compute();
      if (param.states.error.active()) {
        this.node.states.error.set(`param '${param.name()}' error: ${param.states.error.message()}`);
      }
    }
  }
  async evalParams(params) {
    let dirtyParamsCount = 0;
    for (let param of params) {
      if (param.isDirty()) {
        dirtyParamsCount += 1;
      }
    }
    this._promises.length = dirtyParamsCount;
    let i = 0;
    for (let param of params) {
      if (param.isDirty()) {
        this._promises[i] = this._evalParam(param);
        i += 1;
      }
    }
    await Promise.all(this._promises);
    if (this.node.states.error.active()) {
      this.node._setContainer(null);
    }
  }
  paramsEvalRequired() {
    return this._params_node != null && (this._params_node.isDirty() || this._params_added_since_last_params_eval);
  }
  async evalAll() {
    var _a;
    if (this.paramsEvalRequired()) {
      await this.evalParams(this._params_list);
      (_a = this._params_node) == null ? void 0 : _a.removeDirtyState();
      this._params_added_since_last_params_eval = false;
    }
  }
  onParamsCreated(hook_name, hook) {
    if (this._params_created) {
      hook();
    } else {
      if (this._post_create_params_hook_names && this._post_create_params_hook_names.includes(hook_name)) {
        Poly.error(`hook name ${hook_name} already exists`);
        return;
      }
      this._post_create_params_hook_names = this._post_create_params_hook_names || [];
      this._post_create_params_hook_names.push(hook_name);
      this._post_create_params_hooks = this._post_create_params_hooks || [];
      this._post_create_params_hooks.push(hook);
    }
  }
  addOnSceneLoadHook(param_name, method) {
    this._on_scene_load_hook_names = this._on_scene_load_hook_names || [];
    this._on_scene_load_hooks = this._on_scene_load_hooks || [];
    if (!this._on_scene_load_hook_names.includes(param_name)) {
      this._on_scene_load_hook_names.push(param_name);
      this._on_scene_load_hooks.push(method);
    } else {
      Poly.warn(`hook with name ${param_name} already exists`, this.node);
    }
  }
  _runPostCreateParamsHooks() {
    if (this._post_create_params_hooks) {
      for (let hook of this._post_create_params_hooks) {
        hook();
      }
    }
  }
  runOnSceneLoadHooks() {
    if (this._on_scene_load_hooks) {
      for (let hook of this._on_scene_load_hooks) {
        hook();
      }
    }
  }
}
